'use client';

import type { CSSProperties } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { useUiContent } from '@/components/ui-content-provider';
import { useChangePulse } from '@/components/products/live-products-provider';
import { AffiliateLinkTracker } from '../affiliate-link-tracker';
import { RetailerFavicon } from '../retailer-favicon';
import { offerRowCopy } from './offer-copy';
import { useViewerClock } from '@/lib/hooks/use-viewer-clock';
import { ROW_HEIGHT, brandHue, isAmazonOffer, isInfoOffer, type PreparedOffer } from './offers';

type OfferRowProps = {
  offer: PreparedOffer;
  /** Position within the FULL prepared list — reported as `offer_index`, so it
   *  stays comparable whether or not the "Show all" expander was used. */
  offerIndex: number;
  localeDetails: LocaleDetails;
  productTitle: string;
  /** Product's rank on the page — the existing `product_index` dimension. */
  index: number;
  categoryId?: string;
  asin?: string;
  /** Wraps the untagged offer URL in the /go hop. */
  href: string;
  /** Re-arms the buy-intent freeze so a live snapshot cannot swap the price
   *  the reader just committed to. */
  onClick?: () => void;
  /** False while this row is still inside a collapsed pill — see
   *  `RetailerFavicon.active`. Nothing is fetched for a row nobody has seen. */
  active?: boolean;
};

/**
 * One retailer inside the offers panel / bottom sheet.
 *
 * Every price string here arrives PRE-FORMATTED from `prepareOffers` — the
 * currency, the separators and the symbol position all came out of
 * `priceByCountry`, which knows about JPY having no decimals and Swedish
 * putting `kr` after the number. Re-deriving any of it in this component would
 * be a second, quietly divergent formatter. Every SENTENCE arrives from
 * `offerRowCopy`, shared with the pick line and the shelf row, for the same
 * reason.
 *
 * What a row can carry, top to bottom (docs/OFFERS_ENGINE_V2.md section 6):
 * the merchant's mark and name with the "Our pick" / "Best price" chip, or
 * the "Brand store" chip when it earns neither, and the "Deal" chip while an
 * Amazon deal is live; a second line with the stock state and the notes (the
 * "as of" stamp once a price is old enough, "Member price", "Marketplace
 * listing", "Deal ends {time}"); on the price side the price (or "MSRP
 * {price}", "See price at {merchant}", "Official site" for an information
 * link), its was-price, and the honesty chip "{n}% below its usual price".
 */
export function OfferRow({
  offer,
  offerIndex,
  localeDetails,
  productTitle,
  index,
  categoryId,
  asin,
  href,
  onClick,
  active,
}: OfferRowProps) {
  const ui = useUiContent();
  const isAmazon = isAmazonOffer(offer);
  const isSearch = offer.kind === 'search';
  const isInfo = isInfoOffer(offer);
  const outOfStock = offer.availability === 'out_of_stock';
  const viewerLocal = useViewerClock();
  const copy = offerRowCopy(offer, ui, localeDetails, Date.now(), viewerLocal);
  const name = copy.name;

  // Live price arrivals swap a row's number under the reader. A 250ms dip
  // marks it as a deliberate refresh rather than a render glitch — same hook
  // PriceInfo uses, keyed per merchant so one retailer's change never flashes
  // the whole list.
  const pulsing = useChangePulse(`${offer.domainKey}|${offer.priceLabel}`);

  return (
    <AffiliateLinkTracker
      href={href}
      productTitle={productTitle}
      isAmazon={isAmazon}
      locale={localeDetails.languageCode}
      region={localeDetails.region}
      index={index}
      // A search is not the product's listing: its click names no ASIN.
      asin={isAmazon && !isSearch ? asin : undefined}
      categoryId={categoryId}
      onClick={onClick}
      ariaLabel={copy.ariaLabel}
      // The "why this store" sentence and the attribution notice live in the
      // tooltip, never as visible text (spec 6.7, 6.8).
      title={copy.title}
      // Kept from the split CTA this row replaced, so the crawler-facing
      // annotation for "this one is a search, not the recommended listing"
      // survives the design change.
      dataPurpose={isSearch ? 'amazon-search-cta' : undefined}
      // Joins `click_affiliate` rows to the merchant they went to, which is the
      // only way to tell a multi-retailer panel's winners from its decoration.
      extraTrackProps={{
        merchant_domain: offer.domainKey,
        offer_index: offerIndex,
        offer_kind: isSearch
          ? 'amazon-search'
          : isInfo
            ? 'info'
            : (offer.badge ?? (isAmazon ? 'amazon' : 'other')),
      }}
      // Height in PIXELS, from the same constant `panelHeight()` multiplies —
      // this site scales its root font-size with the viewport (globals.css:
      // 15px → ×1.05 → ×1.12 → …), so the `h-[3.5rem]` this used to carry was
      // 55.1px at 1440 and 58.8px at 1600 while the morph was sized for 56.
      // The old `overflow-y: auto` swallowed the difference; without a scroll
      // port it would have clipped the last row on wide screens.
      style={{ height: ROW_HEIGHT }}
      // `px-1.5` completes the panel's 10px gutter (see offers-panel.tsx): the
      // list container spends 4 of it, this row the other 6, so the retailer
      // mark lines up with the Back chevron above and the disclosure icon
      // below while the hover surface still floats 4px inside the panel edge.
      className={`group flex w-full items-center gap-2.5 rounded-xl px-1.5 ${
        outOfStock ? 'opacity-55' : ''
      }`}
    >
      {/* Every retailer, Amazon included, wears its own square mark here.
          Amazon used to get the bundled WORDMARK — a 603×182 logo in a 28px
          box, which rendered ~40px wide and spilled out of the tile it was
          supposed to sit in. The wordmark is right on the CTA pill, where
          there is room for it; in a list of retailer rows the square favicon
          is the mark that fits, and it makes every row the same shape. A row
          whose program requires attribution (spec 6.7) wears the merchant's
          own mark through the same component. */}
      <span className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-md bg-black/5 dark:bg-white/10">
        <RetailerFavicon
          domain={offer.domainKey}
          displayName={name}
          active={active}
          className="h-[1.05rem] w-[1.05rem]"
          fallback={
            <span
              aria-hidden
              // Tinted from the domain (see `brandHue`) so a retailer with no
              // published icon still reads as a mark rather than as a missing
              // one. The hue is deterministic: MEC is the same green on every
              // page, every session.
              style={{ '--brand-h': brandHue(offer.domainKey) } as CSSProperties}
              className="retailer-monogram inline-flex h-[1.05rem] w-[1.05rem] shrink-0 items-center justify-center text-[0.68rem] font-semibold leading-none"
            >
              {(name.charAt(0) || '?').toUpperCase()}
            </span>
          }
        />
      </span>

      <span className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm leading-tight text-zinc-800 dark:text-zinc-100">
            {name}
          </span>
          {/* The "why this store" sentence: right after the name for a screen
              reader, in the tooltip for everyone else (spec 6.8). */}
          {copy.whySentence && <span className="sr-only">{copy.whySentence}</span>}
          {copy.badgeLabel && (
            <span
              className={`shrink-0 whitespace-nowrap rounded-full px-1.5 py-[0.1rem] text-[0.6rem] font-semibold leading-[1.15] ${
                offer.badge === 'bestPlace'
                  ? 'bg-cornflowerblue/10 text-cornflowerblue'
                  : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              }`}
            >
              {copy.badgeLabel}
            </span>
          )}
          {copy.brandStoreLabel && (
            <span className="shrink-0 whitespace-nowrap rounded-full bg-black/[0.06] px-1.5 py-[0.1rem] text-[0.6rem] font-semibold leading-[1.15] text-zinc-600 dark:bg-white/10 dark:text-zinc-300">
              {copy.brandStoreLabel}
            </span>
          )}
          {/* Present whenever the row has a deal, lapsed or not; only the text
              follows the clock, and an empty chip hides itself (see
              OfferRowCopy.hasDealChip). */}
          {copy.hasDealChip && (
            <span
              suppressHydrationWarning
              className="shrink-0 whitespace-nowrap rounded-full bg-orange-500/10 px-1.5 py-[0.1rem] text-[0.6rem] font-semibold leading-[1.15] text-orange-600 empty:hidden dark:text-orange-400"
            >
              {copy.dealLabel}
            </span>
          )}
        </span>
        {/* Always in the markup, even empty. The "as of" stamp appears once a
            price crosses its age threshold, and this panel face ships in the
            SSR response of a cached page: a line that could appear between
            the server's render and hydration must already exist, so only its
            text changes, and that text is allowed to (suppressHydrationWarning
            covers exactly one string child). */}
        <span
          suppressHydrationWarning
          className="truncate text-[0.66rem] leading-tight text-zinc-400 dark:text-zinc-500"
        >
          {copy.subline}
        </span>
      </span>

      <span className="flex shrink-0 flex-col items-end justify-center">
        {copy.priceSlot &&
          (copy.priceSlotIsPrice ? (
            <span
              className={`tabular-nums text-sm font-semibold leading-tight text-zinc-900 transition-opacity duration-150 dark:text-zinc-50 ${
                pulsing ? 'opacity-70' : 'opacity-100'
              }`}
            >
              {copy.priceSlot}
            </span>
          ) : (
            // A label, not a number: "MSRP {price}", "See price at Currys",
            // "Official site". Muted, so it never reads as the row's price.
            <span className="max-w-[9rem] truncate text-right text-[0.72rem] font-medium leading-tight text-zinc-500 dark:text-zinc-400">
              {copy.priceSlot}
            </span>
          ))}
        {offer.originalPriceLabel && (
          <span className="tabular-nums text-[0.66rem] leading-tight text-zinc-400 line-through dark:text-zinc-500">
            {offer.originalPriceLabel}
          </span>
        )}
        {copy.belowUsual && (
          // The honesty chip: the number a two-month observation earns, never a
          // merchant's own claim. The tooltip says exactly how it was computed.
          <span
            title={copy.belowUsual.tooltip}
            className="mt-px whitespace-nowrap rounded-full bg-emerald-500/10 px-1.5 py-[0.1rem] text-[0.58rem] font-semibold leading-[1.15] text-emerald-700 dark:text-emerald-400"
          >
            {copy.belowUsual.label}
          </span>
        )}
      </span>

      <svg
        aria-hidden
        viewBox="0 0 24 24"
        className="h-3.5 w-3.5 shrink-0 text-zinc-300 transition-transform duration-200 group-hover:translate-x-0.5 dark:text-zinc-600"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>

      {copy.srOnly.map((sentence) => (
        <span key={sentence} className="sr-only">
          {sentence}
        </span>
      ))}
    </AffiliateLinkTracker>
  );
}
